// Avatar looks — retextured variants of an existing avatar.
//
// The avatar's model_url is never modified. Meshy retextures the mesh and we
// keep only the base-colour map it returns, applying it as a material override
// at render time. That is what makes a look safe: the rig, the vertex weights
// and every retargeted animation are the ones that were already working.
//
// `enable_original_uv` is the parameter the whole feature rests on. Without it
// Meshy re-unwraps the model and the returned texture no longer corresponds to
// the UVs baked into the avatar's GLB, so the new map would land on the mesh as
// noise.

import { supabase } from '../config/supabase';
import { gathrxrAuthHeader } from '../utils/quotaHeaders';
import { uploadBytesToStorage } from '../utils/uploadToStorage';
import { preserveOriginalHead, wantsHeadChange } from '../avatar/preserveHead';

/**
 * Fetch a remote asset and re-home it in our bucket.
 *
 * Meshy's CDN sends no Access-Control-Allow-Origin header — verified with a
 * curl carrying a browser Origin — so a direct browser fetch of assets.meshy.ai
 * always fails CORS. The existing /api/proxy-fetch function exists for exactly
 * this and allow-lists that host; every Meshy asset goes through it.
 */
async function copyToOurStorage(url: string, key: string, contentType: string): Promise<string> {
  const res = await fetchPossiblyCorsBlocked(url);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  return uploadBytesToStorage(await res.blob(), key, contentType);
}

export function isMeshyAsset(url: string): boolean {
  try { return new URL(url).hostname.endsWith('assets.meshy.ai'); } catch { return false; }
}

export async function fetchPossiblyCorsBlocked(url: string): Promise<Response> {
  if (!isMeshyAsset(url)) return fetch(url);
  return fetch('/api/proxy-fetch', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url }),
  });
}

export interface AvatarLook {
  id: string;
  avatar_id: string;
  owner_id: string;
  name: string;
  prompt: string | null;
  texture_url: string | null;
  thumbnail_url: string | null;
  meshy_task_id: string | null;
  status: 'pending' | 'ready' | 'failed';
  failure_reason: string | null;
  created_at: string;
}

async function meshy(path: string, init?: RequestInit) {
  const headers = await gathrxrAuthHeader();
  const res = await fetch(`/api/meshy/${path}`, {
    ...init,
    headers: { ...headers, 'Content-Type': 'application/json', ...(init?.headers || {}) },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    throw new Error((data as any)?.message || (data as any)?.error?.message || `Meshy ${path} failed`);
  }
  return data as any;
}

/**
 * Retexture repaints the WHOLE model, not a region — Meshy has no concept of
 * "only the jacket". Left to itself it happily reinvents the face, skin and
 * hair along with the clothes, which is the one thing a look must never do.
 *
 * There is no masking parameter to reach for, so identity has to be defended in
 * the prompt: state what must not change before stating what should. It is a
 * mitigation rather than a guarantee — a strong outfit prompt can still bleed
 * into the face — but it moves the common case from "different person" to "same
 * person, new clothes".
 */
export interface LookStyle {
  outfit: string;
  hair?: string;
  makeup?: string;
}

/**
 * The guard is assembled per request rather than fixed. Identity protection is
 * still the default — the face guard is what stopped every generation from
 * reinventing the character — but hair and makeup become opt-in regions: named
 * explicitly, they are asked for; left empty, they are locked exactly as the
 * face is. The same mechanism that prevented accidental hair changes is what
 * now delivers deliberate ones.
 */
function buildLookPrompt(style: LookStyle): string {
  const parts: string[] = ['Keep the face and facial features exactly as they are. Do not alter the head shape.'];

  if (style.hair?.trim()) {
    parts.push(`Change the hair colour to ${style.hair.trim()}, keeping the same hairstyle shape.`);
  } else {
    parts.push('Keep all of the hair, including long strands over the shoulders and down the back, exactly its current colour.');
  }

  if (style.makeup?.trim()) {
    parts.push(`Apply makeup: ${style.makeup.trim()}.`);
  } else {
    parts.push('Keep the skin tone unchanged.');
  }

  const outfit = style.outfit.trim();
  // A retexture can only repaint the garments the model already has — their
  // shapes are geometry. Asking for different clothes ("joggers" on a model
  // wearing a skirt and leg warmers) made Meshy paint the new garments across
  // the old ones, so the result read as the two outfits merged. The request is
  // framed as a recolour of the existing garments instead; different clothes
  // are the Outfit Studio's job (new geometry).
  const outfitPart = outfit
    ? `Recolour the existing clothing only: ${outfit}. Keep every garment's shape, cut, layers and edges exactly as they are, and paint each garment as one whole piece. Do not paint new garments over skin or over other garments.`
    : 'Keep the current clothing exactly as it is.';

  const guard = parts.join(' ') + ' ';
  // 600-char API cap. The guard is the half that matters; the outfit text is
  // what gives way.
  return (guard + outfitPart).slice(0, 600);
}

export async function fetchLooks(avatarId: string): Promise<AvatarLook[]> {
  const { data, error } = await supabase
    .from('avatar_looks')
    .select('*')
    .eq('avatar_id', avatarId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as AvatarLook[]) || [];
}

/**
 * Kick off a retexture. Returns immediately with a pending row — Meshy takes
 * a couple of minutes, and holding the UI open for that is not a design.
 */
export async function createLook(params: {
  avatarId: string;
  modelUrl: string;
  prompt: string;
  hair?: string;
  makeup?: string;
  name?: string;
}): Promise<AvatarLook> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in first.');

  const task = await meshy('v1/retexture', {
    method: 'POST',
    body: JSON.stringify({
      model_url: params.modelUrl,
      text_style_prompt: buildLookPrompt({ outfit: params.prompt, hair: params.hair, makeup: params.makeup }),
      // Non-negotiable: keeps the existing UV layout so the returned map lines
      // up with the avatar's own geometry.
      enable_original_uv: true,
      enable_pbr: false,
      texture_resolution: '2k',
    }),
  });

  const taskId = task?.result || task?.id;
  if (!taskId) throw new Error('Meshy did not return a task id.');

  const described = [
    params.prompt.trim(),
    params.hair?.trim() && `${params.hair.trim()} hair`,
    params.makeup?.trim() && `makeup: ${params.makeup.trim()}`,
  ].filter(Boolean).join(' · ');

  const { data, error } = await supabase
    .from('avatar_looks')
    .insert({
      avatar_id: params.avatarId,
      owner_id: user.id,
      name: params.name?.trim() || described.slice(0, 40),
      prompt: described,
      meshy_task_id: String(taskId),
      status: 'pending',
    })
    .select()
    .single();
  if (error) throw error;
  return data as AvatarLook;
}

/**
 * Poll one pending look and persist the outcome.
 *
 * Returns the updated row, or the original if nothing has changed yet. Safe to
 * call repeatedly: a look that already resolved is returned untouched.
 */
export async function refreshLook(look: AvatarLook): Promise<AvatarLook> {
  // Anything without a usable texture is worth re-checking — including rows an
  // earlier poll marked 'failed'. The first version returned early for every
  // non-pending status, which locked the recovery path behind the exact state
  // that needed recovering: a look mis-marked failed could never be retried,
  // silently, from a button that also only rendered on pending tiles.
  if (look.texture_url) return look;

  let task = look.meshy_task_id
    ? await meshy(`v1/retexture/${look.meshy_task_id}`).catch(() => null)
    : null;

  // Recover from a task id we never stored correctly. Meshy's create response
  // has been seen to return the id under more than one shape, and a look whose
  // id is wrong polls a task that does not exist — forever, silently, with a
  // perfectly good texture sitting on Meshy's side.
  //
  // The list endpoint is the way back: find the task whose prompt matches this
  // look and adopt its id.
  if (!task?.status && look.prompt) {
    const list = await meshy('v1/retexture?page_size=50').catch(() => null);
    const rows: any[] = Array.isArray(list) ? list : (list?.result ?? []);
    // Word overlap rather than substring: the stored prompt is a display
    // string ('denim tee · blonde hair · makeup: red lips') while Meshy holds
    // the assembled guard prompt, so no contiguous slice of one appears in the
    // other. Sharing most distinctive words is the property that survives both
    // formats.
    const words = look.prompt.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 3);
    const match = rows.find((t) => {
      const hay = String(t?.text_style_prompt || '').toLowerCase();
      if (!words.length || !hay) return false;
      const hit = words.filter((w) => hay.includes(w)).length;
      return hit / words.length >= 0.6;
    });
    if (match?.id) {
      task = match;
      // Repair the row so the next poll goes straight there.
      await supabase.from('avatar_looks')
        .update({ meshy_task_id: String(match.id) })
        .eq('id', look.id);
    }
  }

  if (!task?.status) {
    throw new Error('Could not find this generation on Meshy.');
  }

  if (task?.status === 'SUCCEEDED') {
    // The base-colour map is the payload. model_urls.glb is deliberately
    // ignored — swapping in Meshy's re-export would put the rig at risk.
    const tex = task?.texture_urls?.[0]?.base_color || null;
    if (!tex) {
      return persist(look.id, { status: 'failed', failure_reason: 'No texture returned.' });
    }
    // Meshy's asset URLs are SIGNED and expire within days. Storing one as the
    // look's texture means the outfit silently stops loading later — so the
    // texture is copied into our own storage first, and the signed URL is only
    // a fallback if that copy fails (it still buys time to retry).
    const durableTex = await keepFaceAndStore(look, tex)
      .catch(() => copyToOurStorage(tex, `avatars/looks/${look.id}.png`, 'image/png'))
      .catch(() => tex);
    const durableThumb = task?.thumbnail_url
      ? await copyToOurStorage(task.thumbnail_url, `avatars/looks/${look.id}_thumb.png`, 'image/png')
          .catch(() => task.thumbnail_url)
      : null;
    return persist(look.id, {
      status: 'ready',
      texture_url: durableTex,
      thumbnail_url: durableThumb,
    });
  }

  if (task?.status === 'FAILED' || task?.status === 'CANCELED') {
    return persist(look.id, {
      status: 'failed',
      failure_reason: task?.task_error?.message || 'Meshy could not finish this look.',
    });
  }

  return look; // still PENDING or IN_PROGRESS
}

/**
 * Store the look with the avatar's original face (and hair) restored, unless
 * the look asked for new hair or makeup. Meshy repaints the whole texture, so
 * without this every outfit change also changed who the avatar was.
 */
async function keepFaceAndStore(look: AvatarLook, tex: string): Promise<string> {
  if (wantsHeadChange(look.prompt)) throw new Error('head change requested');
  const { data: avatar } = await supabase.from('avatars').select('model_url').eq('id', look.avatar_id).single();
  if (!avatar?.model_url) throw new Error('avatar model not found');
  const res = await fetchPossiblyCorsBlocked(tex);
  if (!res.ok) throw new Error(`fetch ${res.status}`);
  const composed = await preserveOriginalHead(avatar.model_url, await res.blob());
  return uploadBytesToStorage(composed, `avatars/looks/${look.id}_face.png`, 'image/png');
}

async function persist(id: string, patch: Partial<AvatarLook>): Promise<AvatarLook> {
  const { data, error } = await supabase
    .from('avatar_looks')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as AvatarLook;
}

/** Wear a look, or pass null to return to the avatar's original texture. */
export async function applyLook(avatarId: string, lookId: string | null): Promise<void> {
  const { error } = await supabase.rpc('apply_avatar_look', {
    p_avatar_id: avatarId,
    p_look_id: lookId,
  });
  if (error) throw error;
}

export async function deleteLook(lookId: string): Promise<void> {
  const { error } = await supabase.from('avatar_looks').delete().eq('id', lookId);
  if (error) throw error;
}
